/**
 * RecapScreen - Daily summary with Jackpot coin rain
 * Shows participants, blockers, and celebration animation
 */

import { useMemo, useState, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { formatTime } from '../utils';
import './RecapScreen.css';

interface FallingCoin {
  id: number;
  left: number;
  delay: number;
  duration: number;
  fallHeight: number;
}


export function RecapScreen() {
  const { state, actions } = useGame();
  const {
    members,
    absentMembers,
    lateMembers,
    blockers,
    chouquetteDebts,
    issuesWithoutEstimate,
    dailyStartTime,
    selectedBoard,
    selectedSprint,
  } = state;

  // Coin animation state
  const [fallingCoins, setFallingCoins] = useState<FallingCoin[]>([]);
  // Configuration
  const TOTAL_FALLING_COINS = 40;
  const SPAWN_INTERVAL = 150; // ms between each coin spawn

  // Start coin animation on mount
  useEffect(() => {
    // Spawn falling coins progressively
    const coinTimers: NodeJS.Timeout[] = [];
    for (let i = 0; i < TOTAL_FALLING_COINS; i++) {
      const timer = setTimeout(() => {
        setFallingCoins(prev => [
          ...prev,
          {
            id: i,
            left: 10 + Math.random() * 80, // 10% to 90% of screen width
            delay: 0,
            duration: 2 + Math.random() * 1.5, // 2-3.5s fall time
            fallHeight: 70 + Math.random() * 15, // 70-85vh
          }
        ]);
      }, 800 + i * SPAWN_INTERVAL);
      coinTimers.push(timer);
    }

    return () => {
      coinTimers.forEach(t => clearTimeout(t));
    };
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const endTime = new Date();
    const duration = dailyStartTime
      ? Math.floor((endTime.getTime() - dailyStartTime.getTime()) / 1000)
      : 0;

    const presentMembers = members.filter(
      (m) =>
        !absentMembers.some((a) => a.accountId === m.accountId) &&
        !lateMembers.some((l) => l.accountId === m.accountId)
    );

    const totalIssues = members.reduce((sum, m) => sum + m.issues.length, 0);

    return {
      duration,
      totalMembers: members.length,
      presentCount: presentMembers.length,
      absentCount: absentMembers.length,
      lateCount: lateMembers.length,
      blockerCount: blockers.length,
      totalIssues,
      unestimatedCount: issuesWithoutEstimate.length,
      chouquetteCount: chouquetteDebts.length,
    };
  }, [members, absentMembers, lateMembers, blockers, issuesWithoutEstimate, chouquetteDebts, dailyStartTime]);

  // Calculate story points by status category
  const sprintProgress = useMemo(() => {
    let doneSP = 0;
    let inProgressSP = 0;
    let todoSP = 0;

    // Iterate through all members' issues
    members.forEach(member => {
      member.issues.forEach(issue => {
        const sp = issue.fields.storyPoints || 0;
        const categoryKey = issue.fields.status.statusCategory.key;

        if (categoryKey === 'done') {
          doneSP += sp;
        } else if (categoryKey === 'indeterminate') {
          inProgressSP += sp;
        } else {
          // 'new' or any other status
          todoSP += sp;
        }
      });
    });

    const totalSP = doneSP + inProgressSP + todoSP;
    const donePercent = totalSP > 0 ? (doneSP / totalSP) * 100 : 0;
    const inProgressPercent = totalSP > 0 ? (inProgressSP / totalSP) * 100 : 0;
    const todoPercent = totalSP > 0 ? (todoSP / totalSP) * 100 : 0;

    // Calculate theoretical done percentage based on sprint dates
    let theoreticalPercent = 0;
    let theoreticalSP = 0;
    if (selectedSprint?.startDate && selectedSprint?.endDate && totalSP > 0) {
      const startDate = new Date(selectedSprint.startDate);
      const endDate = new Date(selectedSprint.endDate);
      const today = new Date();

      const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      const daysPassed = Math.max(0, (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      // Clamp to 0-100%
      theoreticalPercent = Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));
      theoreticalSP = Math.round((totalSP * theoreticalPercent) / 100);
    }

    return {
      doneSP,
      inProgressSP,
      todoSP,
      totalSP,
      donePercent,
      inProgressPercent,
      todoPercent,
      theoreticalPercent,
      theoreticalSP,
    };
  }, [members, selectedSprint]);

  // Handle new daily
  const handleNewDaily = () => {
    actions.reset();
    actions.setPhase('BOOT');
  };

  return (
    <div className="screen recap-screen">

      {/* Falling Coins */}
      <div className="falling-coins">
        {fallingCoins.map(coin => (
          <div
            key={coin.id}
            className="coin-falling"
            style={{
              left: `${coin.left}%`,
              '--fall-delay': `${coin.delay}s`,
              '--fall-duration': `${coin.duration}s`,
              '--fall-height': `${coin.fallHeight}vh`,
            } as React.CSSProperties}
          >
            <div className="coin-inner">
              <div className="coin-face">$</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <h2 className="title">DAILY COMPLETE!</h2>

      <div className="recap-header">
        <span className="recap-board">{selectedBoard?.name}</span>
        <span className="recap-sprint">{selectedSprint?.name}</span>
      </div>

      {/* Duration */}
      <div className="recap-duration">
        <span className="duration-label">TOTAL TIME</span>
        <span className="duration-value">{formatTime(stats.duration)}</span>
      </div>

      {/* Sprint Progress Bar */}
      {sprintProgress.totalSP > 0 && (
        <div className="sprint-progress">
          <div className="progress-header">
            <span className="progress-title">SPRINT XP</span>
            <span className="progress-total">{sprintProgress.totalSP} SP</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-segment progress-done"
              style={{ width: `${sprintProgress.donePercent}%` }}
            />
            <div
              className="progress-segment progress-inprogress"
              style={{ width: `${sprintProgress.inProgressPercent}%` }}
            />
            <div
              className="progress-segment progress-todo"
              style={{ width: `${sprintProgress.todoPercent}%` }}
            />
            {/* Theoretical target line */}
            {sprintProgress.theoreticalPercent > 0 && (
              <div
                className="progress-target-line"
                style={{ left: `${sprintProgress.theoreticalPercent}%` }}
              />
            )}
          </div>
          <div className="progress-legend">
            <div className="legend-item legend-done">
              <span className="legend-dot" />
              <span className="legend-label">DONE</span>
              <span className="legend-value">{sprintProgress.doneSP} SP</span>
            </div>
            <div className="legend-item legend-inprogress">
              <span className="legend-dot" />
              <span className="legend-label">IN PROGRESS</span>
              <span className="legend-value">{sprintProgress.inProgressSP} SP</span>
            </div>
            <div className="legend-item legend-todo">
              <span className="legend-dot" />
              <span className="legend-label">TODO</span>
              <span className="legend-value">{sprintProgress.todoSP} SP</span>
            </div>
            {sprintProgress.theoreticalPercent > 0 && (
              <div className="legend-item legend-target">
                <span className="legend-dot" />
                <span className="legend-label">TARGET</span>
                <span className="legend-value">{sprintProgress.theoreticalSP} SP</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statistics Grid */}
      <div className="stats-grid">
        <div className="stat-card stat-present">
          <span className="stat-value">{stats.presentCount}</span>
          <span className="stat-label">PRESENT</span>
        </div>
        <div className="stat-card stat-absent">
          <span className="stat-value">{stats.absentCount}</span>
          <span className="stat-label">ABSENT</span>
        </div>
        <div className="stat-card stat-late">
          <span className="stat-value">{stats.lateCount}</span>
          <span className="stat-label">LATE</span>
        </div>
        <div className="stat-card stat-issues">
          <span className="stat-value">{stats.totalIssues}</span>
          <span className="stat-label">ISSUES</span>
        </div>
        <div className="stat-card stat-unestimated">
          <span className="stat-value">{stats.unestimatedCount}</span>
          <span className="stat-label">NO ESTIMATE</span>
        </div>
        <div className="stat-card stat-blockers">
          <span className="stat-value">{stats.blockerCount}</span>
          <span className="stat-label">BLOCKERS</span>
        </div>
      </div>

      {/* Blockers List */}
      {blockers.length > 0 && (
        <div className="recap-section">
          <h3 className="section-title">BLOCKERS</h3>
          <div className="blockers-list">
            {blockers.map((blocker, index) => (
              <div key={index} className="blocker-item">
                <span className="blocker-member">{blocker.memberName}</span>
                <span className="blocker-description">{blocker.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chouquette Debts */}
      {chouquetteDebts.length > 0 && (
        <div className="recap-section chouquette-section">
          <h3 className="section-title">🥐 CHOUQUETTE TIME</h3>
          <div className="chouquette-list">
            {chouquetteDebts.map((debt, index) => (
              <div key={index} className="chouquette-item">
                <span className="chouquette-member">{debt.memberName}</span>
                <span className="chouquette-count">owes chouquettes!</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Absent Members */}
      {absentMembers.length > 0 && (
        <div className="recap-section">
          <h3 className="section-title">ABSENT</h3>
          <div className="absent-list">
            {absentMembers.map((member) => (
              <span key={member.accountId} className="absent-name">
                {member.displayName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="recap-actions">
        <button className="action-button action-new" onClick={handleNewDaily}>
          [START] NEW DAILY
        </button>
      </div>

    </div>
  );
}

export default RecapScreen;
